import { useEffect, useRef } from 'react';
import { cn } from '../../utils/utils';

interface CheckboxProps {
    checked: boolean;
    onChange: (checked: boolean) => void;
    /**
     * The "some but not all" state of a select-all box. Only the DOM property
     * can show it (there is no HTML attribute), hence the ref below.
     */
    indeterminate?: boolean;
    /** Accessible name; required because a table checkbox has no visible label. */
    ariaLabel: string;
    disabled?: boolean;
    className?: string;
}

/**
 * The design system's checkbox: a native input, ticked in the tenant primary
 * (`accent-primary`), no MUI. Native so keyboard, focus and screen readers
 * behave without extra code.
 */
const Checkbox = ({
    checked,
    onChange,
    indeterminate = false,
    ariaLabel,
    disabled = false,
    className,
}: Readonly<CheckboxProps>) => {
    const ref = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (ref.current) ref.current.indeterminate = indeterminate && !checked;
    }, [indeterminate, checked]);

    return (
        <input
            ref={ref}
            type={'checkbox'}
            className={cn('h-4 w-4 cursor-pointer accent-primary disabled:cursor-not-allowed', className)}
            checked={checked}
            disabled={disabled}
            aria-label={ariaLabel}
            aria-checked={indeterminate && !checked ? 'mixed' : checked}
            onChange={(event) => onChange(event.target.checked)}
            // A row checkbox sits inside clickable rows (the series row
            // toggles open on click); ticking it must not also do that.
            onClick={(event) => event.stopPropagation()}
        />
    );
};

export default Checkbox;
